import React, { useState, useMemo, useCallback } from 'react';
import { useFinanceDataContext } from '../../contexts/FinanceDataContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useTheme } from '../../contexts/ThemeContext';
import { formatSmartCurrency } from '../../utils/formatCurrency';
import moment from 'moment';

const PRESET_COLORS = [
  '#4882F7', '#3FD082', '#FF7283', '#FFDE67', '#9B59B6',
  '#0891b2', '#e11d48', '#65a30d', '#c026d3', '#ea580c',
  '#795548', '#607d8b',
];
const PRESET_EMOJIS = [
  '🏠', '🍔', '🚗', '⚡', '🎬', '🎓', '🏥', '👤',
  '🛒', '✈️', '🎁', '💼', '🐕', '👶', '💪', '📱',
];

const BudgetsPage = () => {
  const {
    budgets, getItemsForDateRange,
    addBudgetCategory, updateBudgetCategory, deleteBudgetCategory,
    archiveBudgetCategory, unarchiveBudgetCategory,
  } = useFinanceDataContext();
  const { currencyCode } = useCurrency();
  const { theme } = useTheme();
  const [showAddForm, setShowAddForm] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [activeTab, setActiveTab] = useState('categories');
  const [editingId, setEditingId] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [form, setForm] = useState({ title: '', emoji: '📋', color: '#4882F7', amount: '' });

  const fmt = useCallback((cents, mode = 'long') => formatSmartCurrency(cents / 100, { mode, currencyCode }), [currencyCode]);

  const { monthStart, monthEnd } = useMemo(() => ({
    monthStart: moment().startOf('month').toDate(),
    monthEnd: moment().endOf('month').toDate(),
  }), []);

  const periodLabel = useMemo(() => {
    const start = moment(monthStart).format('ddd, MMM D');
    const end = moment(monthEnd).format('ddd, MMM D');
    return { start, end };
  }, [monthStart, monthEnd]);

  const monthExpenses = useMemo(() => {
    return getItemsForDateRange(monthStart, monthEnd).filter((i) => i.type === 'expense' && !i.deleted);
  }, [getItemsForDateRange, monthStart, monthEnd]);

  const activeBudgets = useMemo(() => budgets.filter((b) => b.type === 'budget' && !b.isArchived), [budgets]);
  const archivedBudgets = useMemo(() => budgets.filter((b) => b.type === 'budget' && b.isArchived), [budgets]);

  const budgetSpending = useMemo(() => {
    const s = {};
    monthExpenses.forEach((exp) => {
      const catId = exp.budgetCategoryId;
      if (!catId) return;
      if (!s[catId]) s[catId] = { total: 0, count: 0, items: [] };
      s[catId].total += Math.abs(exp.amount) * 100;
      s[catId].count += 1;
      s[catId].items.push(exp);
    });
    return s;
  }, [monthExpenses]);

  const totalAssigned = useMemo(() => activeBudgets.reduce((s, b) => s + (b.amount || 0), 0), [activeBudgets]);
  const totalSpent = useMemo(() => Object.values(budgetSpending).reduce((s, v) => s + v.total, 0), [budgetSpending]);
  const remaining = totalAssigned - totalSpent;

  const handleAdd = async () => {
    if (!form.title.trim()) return;
    await addBudgetCategory({ title: form.title.trim(), emoji: form.emoji, color: form.color, amount: parseFloat(form.amount) || 0 });
    setForm({ title: '', emoji: '📋', color: '#4882F7', amount: '' });
    setShowAddForm(false);
  };

  const handleSaveAmount = async (budget) => {
    const v = parseFloat(editAmount);
    if (isNaN(v)) return;
    await updateBudgetCategory(budget.id, { amount: Math.round(v * 100) });
    setEditingId(null);
    setEditAmount('');
  };

  // Available color matching mobile screenshot
  const getAvailColor = (avail, assigned) => {
    if (avail < 0) return '#FF7283';
    if (assigned === 0) return '#999';
    const pct = (avail / assigned) * 100;
    if (pct <= 10) return '#FF7283';
    if (pct <= 30) return '#FFDE67';
    return '#3FD082';
  };

  return (
    <div style={{ backgroundColor: theme.backgroundColor, minHeight: '100%' }}>
      {/* Pay Period header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 8px' }}>
        <span style={{ fontSize: 13, color: theme.tertiaryText }}>{periodLabel.start}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13 }}>📅</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: theme.textColor }}>Pay Period</span>
        </div>
        <span style={{ fontSize: 13, color: theme.tertiaryText }}>{periodLabel.end}</span>
      </div>

      {/* Summary pills */}
      <div style={{ display: 'flex', gap: 8, padding: '12px 20px 16px', justifyContent: 'center' }}>
        <SummaryPill label="Total Budget" value={fmt(totalAssigned)} bg="#EFF6FF" color="#4882F7" />
        <SummaryPill label="Spent" value={fmt(totalSpent)} bg="#FFF1F2" color="#FF7283" />
        <SummaryPill label="Remaining" value={fmt(remaining)} bg="#ECFDF5" color={remaining >= 0 ? '#3FD082' : '#FF7283'} />
      </div>

      {/* Category tabs */}
      <div style={{ display: 'flex', gap: 0, padding: '0 20px', borderBottom: `1px solid ${theme.lightBorder}`, marginBottom: 4 }}>
        {['Categories', 'Spent', 'Upcoming'].map((t) => {
          const key = t.toLowerCase();
          const active = activeTab === key;
          return (
            <button key={t} onClick={() => setActiveTab(key)} style={{
              padding: '8px 16px', border: 'none', cursor: 'pointer',
              backgroundColor: 'transparent', fontSize: 14, fontWeight: active ? 600 : 400,
              color: active ? '#222' : '#999',
              borderBottom: active ? '2px solid #222' : '2px solid transparent',
              marginBottom: -1,
            }}>{t}</button>
          );
        })}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: theme.tertiaryText, textTransform: 'uppercase', alignSelf: 'center', letterSpacing: 0.5 }}>Available</span>
      </div>

      {/* Budget category rows */}
      <div style={{ padding: '0 0 100px' }}>
        {activeBudgets.map((budget) => {
          const assigned = budget.amount || 0;
          const spending = budgetSpending[budget.id];
          const spent = spending ? spending.total : 0;
          const avail = assigned - spent;
          const pctUsed = assigned > 0 ? Math.round((spent / assigned) * 100) : 0;
          const progress = assigned > 0 ? Math.min(100, (spent / assigned) * 100) : 0;
          const isEditing = editingId === budget.id;
          const budgetColor = budget.color || '#4882F7';

          return (
            <div key={budget.id} style={{ padding: '14px 20px', borderBottom: `1px solid ${theme.lightBorder}` }}>
              {/* Top row: emoji + name + available */}
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ fontSize: 24, marginRight: 12, width: 32, textAlign: 'center' }}>
                  {budget.emoji || '📋'}
                </span>
                <span style={{ flex: 1, fontSize: 16, fontWeight: 600, color: theme.textColor }}>
                  {budget.title}
                </span>
                {isEditing ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="number" value={editAmount} onChange={(e) => setEditAmount(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveAmount(budget); if (e.key === 'Escape') { setEditingId(null); setEditAmount(''); } }}
                      autoFocus step="0.01"
                      style={{ width: 80, padding: '2px 6px', border: '2px solid #4882F7', borderRadius: 6, fontSize: 16, fontWeight: 700, textAlign: 'right', outline: 'none' }} />
                    <button onClick={() => handleSaveAmount(budget)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3FD082', fontWeight: 700, fontSize: 16 }}>✓</button>
                    <button onClick={() => { setEditingId(null); setEditAmount(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.tertiaryText }}>✕</button>
                  </div>
                ) : (
                  <div style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => { setEditingId(budget.id); setEditAmount((assigned / 100).toFixed(2)); }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: getAvailColor(avail, assigned) }}>
                      {fmt(avail, 'short')}
                    </span>
                    <span style={{ fontSize: 14, color: theme.disabledText, marginLeft: 2 }}>
                      /{fmt(assigned, 'short')}
                    </span>
                  </div>
                )}
              </div>

              {/* Progress bar */}
              <div style={{ height: 3, backgroundColor: theme.badgeBg, borderRadius: 2, margin: '8px 0 6px 44px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${progress}%`, backgroundColor: budgetColor, borderRadius: 2, transition: 'width 0.3s' }} />
              </div>

              {/* Detail line */}
              <div style={{ fontSize: 13, color: theme.tertiaryText, marginLeft: 44, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: pctUsed > 100 ? '#FF7283' : '#999' }}>{pctUsed}% used</span>
                <span>{fmt(spent, 'short')} planned</span>
                {/* Expense emoji chips */}
                {spending && spending.items.slice(0, 4).map((exp, i) => (
                  <span key={i} style={{ fontSize: 14 }}>{exp.emoji || '💳'}</span>
                ))}
                {spending && spending.count > 4 && (
                  <span style={{ fontSize: 12, color: theme.disabledText }}>{spending.count - 4}d</span>
                )}
              </div>
            </div>
          );
        })}

        {/* Empty state */}
        {activeBudgets.length === 0 && !showAddForm && (
          <div style={{ textAlign: 'center', padding: '60px 40px' }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: theme.textColor, marginBottom: 8 }}>No Budget Categories</div>
            <div style={{ fontSize: 15, color: theme.tertiaryText, lineHeight: '22px' }}>Create categories to track your spending</div>
          </div>
        )}

        {/* Add form */}
        {showAddForm ? (
          <div style={{ margin: '16px 20px', borderRadius: 16, padding: 20, border: `1px solid ${theme.borderColor}`, backgroundColor: theme.surfaceBackground }}>
            <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 16, color: theme.textColor }}>New Budget Category</div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Name</label>
              <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g., Groceries, Rent" maxLength={20} style={inputStyle} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Monthly Budget</label>
              <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" step="0.01" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Emoji</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {PRESET_EMOJIS.map((e) => (
                  <button key={e} onClick={() => setForm({ ...form, emoji: e })} style={{ width: 38, height: 38, borderRadius: 10, fontSize: 20, cursor: 'pointer', border: form.emoji === e ? '2px solid #4882F7' : '1px solid #E5E7EB', backgroundColor: form.emoji === e ? '#EFF6FF' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{e}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Color</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {PRESET_COLORS.map((c) => (
                  <button key={c} onClick={() => setForm({ ...form, color: c })} style={{ width: 28, height: 28, borderRadius: 14, cursor: 'pointer', backgroundColor: c, border: form.color === c ? '3px solid #222' : '2px solid transparent', boxSizing: 'border-box' }} />
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleAdd} style={{ flex: 1, padding: 12, backgroundColor: '#222', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer' }}>Add Category</button>
              <button onClick={() => { setShowAddForm(false); setForm({ title: '', emoji: '📋', color: '#4882F7', amount: '' }); }} style={{ padding: '12px 20px', backgroundColor: theme.backgroundColor, color: theme.tertiaryText, border: `1px solid ${theme.borderColor}`, borderRadius: 12, fontSize: 16, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={{ padding: '16px 20px' }}>
            <button onClick={() => setShowAddForm(true)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: 14, border: `2px dashed ${theme.borderColor}`, borderRadius: 16, backgroundColor: 'transparent', cursor: 'pointer', color: theme.tertiaryText, fontSize: 15, fontWeight: 500 }}>+ Add Category</button>
          </div>
        )}

        {/* Archived */}
        {archivedBudgets.length > 0 && (
          <div style={{ padding: '8px 20px 32px' }}>
            <button onClick={() => setShowArchived(!showArchived)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: theme.tertiaryText, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10 }}>{showArchived ? '▼' : '▶'}</span> Archived ({archivedBudgets.length})
            </button>
            {showArchived && archivedBudgets.map((b) => (
              <div key={b.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${theme.lightBorder}`, opacity: 0.5 }}>
                <span style={{ marginRight: 10, fontSize: 18 }}>{b.emoji || '📋'}</span>
                <span style={{ flex: 1, fontSize: 15, color: theme.tertiaryText }}>{b.title}</span>
                <button onClick={() => unarchiveBudgetCategory(b.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4882F7', fontSize: 13, fontWeight: 600, marginRight: 10 }}>Restore</button>
                <button onClick={() => { if (window.confirm(`Delete "${b.title}"?`)) deleteBudgetCategory(b.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.disabledText, fontSize: 13 }} onMouseEnter={(e) => (e.currentTarget.style.color = '#EF4444')} onMouseLeave={(e) => (e.currentTarget.style.color = '#D1D5DB')}>Delete</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const SummaryPill = ({ label, value, bg, color }) => (
  <div style={{ flex: 1, backgroundColor: bg, borderRadius: 12, padding: '10px 12px', textAlign: 'center' }}>
    <div style={{ fontSize: 11, fontWeight: 500, color: '#999', marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
  </div>
);

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#999', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 };
const inputStyle = { width: '100%', padding: '12px 14px', border: '1px solid #999', borderRadius: 10, fontSize: 16, outline: 'none', boxSizing: 'border-box', color: 'inherit', backgroundColor: 'transparent' };

export default BudgetsPage;
